import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  IGetDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/GetDatabase/types/IGetDatabaseRequestInterfaces'
import {
  IGetDatabasesRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/GetDatabases/types/IGetDatabasesRequestInterfaces'
import {
  ICreateDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/CreateDatabase/types/ICreateDatabaseRequestInterfaces'
import {
  IUpdateDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/UpdateDatabase/types/IUpdateDatabaseRequestInterfaces'
import {
  IDeleteDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/DeleteDatabase/types/IDeleteDatabaseRequestInterfaces'
import {mapDatabaseResponseInstance} from '@toolify/client/src/modules/api/singletons/mapDatabaseResponseInstance'
import {DatabaseTemplateType} from '@toolify/server/src/services/DatabaseTemplateService/enum/DatabaseTemplateType'
import {
  ICreateDatabaseByTemplateRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/CreateDatabaseByTemplate/types/ICreateDatabaseByTemplateRequestInterfaces'
import {
  ICreateDatabaseByPromptRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/CreateDatabaseByPrompt/types/ICreateDatabaseByPromptRequestInterfaces'
import {
  IDatabaseResponseInstance,
} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabaseResponseInstance'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {IViewFilterQuery} from '@toolify/server/src/repositories/ViewRepository/types/IViewFilterQuery'
import {
  IGenerateFilterByPromptRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/GenerateFilterByPrompt/types/IGenerateFilterByPromptRequestInterfaces'
import {
  IUnarchiveDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/UnarchiveDatabase/types/IUnarchiveDatabaseRequestInterfaces'
import {
  IExportDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/ExportDatabase/types/IExportDatabaseRequestInterfaces'
import {ExportFileTypes} from '@toolify/server/src/jobs/ExportDatabaseJob/types/ExportFileTypes'
import {
  IConvertFileToSuggestedDataInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/ConvertFileToSuggestedData/types/IConvertFileToSuggestedDataInterfaces.js'
import {
  IImportDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/ImportDatabase/types/IImportDatabaseRequestInterfaces.js'
import {IImportDatabaseFile} from '@toolify/server/src/jobs/ImportDatabaseFromFileJob/types/IImportDatabaseFile.js'
import {
  IGetDatabaseTemplatesRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/GetDatabaseTemplates/types/IGetDatabaseTemplatesRequestInterfaces'
import {
  IDuplicateDatabaseRequestInterfaces,
} from '@toolify/server/src/controllers/DatabaseController/validators/DuplicateDatabase/types/IDuplicateDatabaseRequestInterfaces'

export const useDatabaseApiStore = defineStore('databaseApi', {
  actions: {
    async createDatabase(workspaceId: string, title: string, resourceGroupId: string = null,resourceSpaceId:string = null, isPublic = false, collectionItemParentId: string = null): Promise<ICreateDatabaseRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {database} = await apiRequestStore.post<ICreateDatabaseRequestInterfaces>(`/workspace/${workspaceId}/database`, {
        data: {
          title: title,
          resourceGroupId,
          resourceSpaceId,
          collectionItemParentId,
          isPublic,
        },
      })
      return {
        database: mapDatabaseResponseInstance(database),
      }
    },

    async createDatabaseByTemplate(workspaceId: string, title: string, templateType: DatabaseTemplateType, resourceGroupId: string = null, resourceSpaceId: string = null, collectionItemParentId: string = null): Promise<ICreateDatabaseByTemplateRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const res = await apiRequestStore.post<ICreateDatabaseByTemplateRequestInterfaces>(`/workspace/${workspaceId}/database-by-template`, {
        data: {
          title: title || (new Date).toLocaleString(),
          templateType,
          resourceGroupId,
          resourceSpaceId,
          collectionItemParentId,
        },
      })
      return {
        databases: res.databases.map(database => mapDatabaseResponseInstance(database)),
      }
    },

    async getDatabaseTemplates(workspaceId: string): Promise<IGetDatabaseTemplatesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.get<IGetDatabaseTemplatesRequestInterfaces>(`/workspace/${workspaceId}/database-templates`)
    },

    async createDatabaseByPrompt(workspaceId: string, title: string, prompt: string): Promise<IDatabaseResponseInstance[]> {
      const apiRequestStore = useApiRequestStore()
      const res = await apiRequestStore.post<ICreateDatabaseByPromptRequestInterfaces>(`/workspace/${workspaceId}/database-by-prompt`, {
        data: {
          title: title || (new Date).toLocaleString(),
          prompt,
        },
      })
      return res.databases.map(database => {
        return mapDatabaseResponseInstance(database)
      })
    },

    async convertFileToUniqueSuggestedData(databaseId: string, file: File): Promise<IConvertFileToSuggestedDataInterfaces['response']> {
      const formData = new FormData()

      if (file.type === 'text/csv' || file.type === 'application/csv') {
        formData.append('file', file, 'file.csv')
      } else if (file.type === 'application/json') {
        formData.append('file', file, 'file.json')
      } else {
        formData.append('file', file)
      }

      formData.append('existingDatabaseId', databaseId)

      const apiRequestStore = useApiRequestStore()

      return await apiRequestStore.post<IConvertFileToSuggestedDataInterfaces>('/database/convert-file-to-data', {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        data: formData,
      })
    },

    async importDatabase(databaseId: string, fileId: string, convertedFileData: IImportDatabaseFile[]): Promise<IImportDatabaseRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IImportDatabaseRequestInterfaces>(`/database/${databaseId}/import`, {
        data: {
          convertedFileData,
          fileId,
        },
      })
    },

    async getDatabase(databaseId: string): Promise<IGetDatabaseRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {database} = await apiRequestStore.get<IGetDatabaseRequestInterfaces>(`/database/${databaseId}`)
      return {
        database: mapDatabaseResponseInstance(database),
      }
    },

    async updateDatabase(databaseId: string, database: IUpdateDatabaseRequestInterfaces['body']): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.put<IUpdateDatabaseRequestInterfaces>(`/database/${databaseId}`, {
        data: database,
      })
    },

    async deleteDatabase(databaseId: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.delete<IDeleteDatabaseRequestInterfaces>(`/database/${databaseId}`)
    },

    async getDatabases(workspaceId: string): Promise<IGetDatabasesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {databases} = await apiRequestStore.get<IGetDatabasesRequestInterfaces>(`/workspace/${workspaceId}/databases`)
      const mappedDatabases = databases.map(database => {
        return mapDatabaseResponseInstance(database)
      })
      return {
        databases: mappedDatabases,
      }
    },

    async getDatabaseWithProperty(workspaceId: string, propertyValueType?: string): Promise<IGetDatabasesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {databases} = await apiRequestStore.get<IGetDatabasesRequestInterfaces>(`/workspace/${workspaceId}/databases-with-property` ,{
        params: {
          propertyValueType,
        },
      })
      const mappedDatabases = databases.map(database => {
        return mapDatabaseResponseInstance(database)
      })
      return {
        databases: mappedDatabases,
      }
    },

    async generateFilterByPrompt(databaseId: string, prompt: string): Promise<IFilter> {
      const apiRequestStore = useApiRequestStore()
      const {filter} = await apiRequestStore.post<IGenerateFilterByPromptRequestInterfaces>(`/database/${databaseId}/filter-by-prompt`, {
        data: {
          prompt,
        },
      })
      return filter
    },

    async unarchiveDatabase(databaseId: string): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.post<IUnarchiveDatabaseRequestInterfaces>(`/database/${databaseId}/unarchive`)
    },

    async exportDatabase(databaseId: string, exportType: ExportFileTypes, filterQuery: IViewFilterQuery): Promise<void> {
      const apiRequestStore = useApiRequestStore()
      await apiRequestStore.post<IExportDatabaseRequestInterfaces>(`/database/${databaseId}/exportDatabase`, {
        data: {
          exportType,
          filterQuery,
        },
      })
    },

    async duplicateDatabase(databaseId: string, title?: string): Promise<IDuplicateDatabaseRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {jobId} = await apiRequestStore.post<IDuplicateDatabaseRequestInterfaces>(`/database/${databaseId}/duplicate`, {
        data: {
          title,
        },
      })
      return {
        jobId,
      }
    },
  },
})
